// Voix des dialogues, synthétisées avec Web Audio : un petit son court par syllabe écrite, au timbre et à la hauteur
// de la voix. Joué par le jeu (boîte de dialogue) et par l'administration (essai de la voix d'un PNJ).
import type { Voice, VoiceTone } from '../../shared/voices.ts';

interface ToneSpec {
  /** Fréquence de base (Hz), avant le décalage de la voix. */
  base: number;
  wave: OscillatorType;
  /** Durée d'un son (secondes). */
  duration: number;
  volume: number;
  /** Écart de hauteur d'une lettre à l'autre (demi-tons, au plus). */
  spread: number;
  /** Glissement de hauteur pendant le son (demi-tons ; négatif : il retombe). */
  glide: number;
  /** Filtre qui arrondit (passe-bas) ou pince (passe-bande) le timbre ; absent : son brut. */
  filter?: { type: BiquadFilterType; freq: number; q?: number };
}

const TONES: Readonly<Record<Exclude<VoiceTone, 'none'>, ToneSpec>> = {
  soft: { base: 330, wave: 'triangle', duration: 0.06, volume: 0.22, spread: 3, glide: 0 },
  bright: { base: 520, wave: 'square', duration: 0.045, volume: 0.06, spread: 3, glide: 1, filter: { type: 'lowpass', freq: 2600 } },
  deep: { base: 140, wave: 'sawtooth', duration: 0.075, volume: 0.12, spread: 2, glide: 0, filter: { type: 'lowpass', freq: 700 } },
  nasal: { base: 290, wave: 'sawtooth', duration: 0.05, volume: 0.14, spread: 3, glide: 0, filter: { type: 'bandpass', freq: 1300, q: 4 } },
  robot: { base: 240, wave: 'square', duration: 0.05, volume: 0.05, spread: 0, glide: 0, filter: { type: 'lowpass', freq: 1800 } },
  cry: { base: 420, wave: 'sawtooth', duration: 0.1, volume: 0.09, spread: 4, glide: -5, filter: { type: 'lowpass', freq: 2200 } },
};

/** Hauteur propre à chaque voyelle (en part de l'écart du timbre) : la voix « chante » un peu ce qu'elle dit. */
const VOWELS: Readonly<Record<string, number>> = { a: 0, e: -0.5, i: 1, o: -1, u: 0.5, y: 0.75 };

/** Lettre qui fait parler la voix (les espaces et la ponctuation se taisent). */
export const isVoiced = (char: string): boolean => /\p{L}|\p{N}/u.test(char);

/** Joue le son de `char` dans la voix `voice`, vers `destination` (rien pour une voix muette). */
export function speakLetter(ctx: AudioContext, destination: AudioNode, voice: Voice, char: string): void {
  if (voice.tone === 'none') return;
  const tone = TONES[voice.tone];
  const letter = char.normalize('NFD').charAt(0).toLowerCase();
  const shape = VOWELS[letter] ?? ((letter.charCodeAt(0) % 5) - 2) / 2;
  const semitones = voice.pitch + shape * tone.spread;
  const freq = tone.base * 2 ** (semitones / 12);
  const t0 = ctx.currentTime + 0.005;
  const t1 = t0 + tone.duration;

  const oscillator = ctx.createOscillator();
  oscillator.type = tone.wave;
  oscillator.frequency.setValueAtTime(freq, t0);
  if (tone.glide) oscillator.frequency.exponentialRampToValueAtTime(freq * 2 ** (tone.glide / 12), t1);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(tone.volume, t0 + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, t1);
  let chain: AudioNode = oscillator;
  if (tone.filter) {
    const filter = ctx.createBiquadFilter();
    filter.type = tone.filter.type;
    filter.frequency.value = tone.filter.freq;
    if (tone.filter.q) filter.Q.value = tone.filter.q;
    chain = chain.connect(filter);
  }
  chain.connect(gain).connect(destination);
  oscillator.start(t0);
  oscillator.stop(t1 + 0.02);
}
